import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import "lenis/dist/lenis.css";

export const EASE = [0.22, 1, 0.36, 1];

let lenisInstance = null;
export const getLenis = () => lenisInstance;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const SmoothScroll = ({ children }) => {
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    lenisInstance = new Lenis({
      lerp: 0.09,
      smoothWheel: true,
      autoRaf: true,
      allowNestedScroll: true,
      anchors: false,
    });
    return () => {
      lenisInstance?.destroy();
      lenisInstance = null;
    };
  }, []);
  return children;
};

export const scrollToTarget = (target, options = {}) => {
  const lenis = getLenis();
  if (lenis) {
    lenis.scrollTo(target, { offset: -96, ...options });
    return;
  }
  if (typeof target === "number") {
    window.scrollTo({ top: target, behavior: options.immediate ? "instant" : "smooth" });
  } else if (target?.scrollIntoView) {
    target.scrollIntoView({ behavior: options.immediate ? "instant" : "smooth", block: "start" });
  }
};

// Masked, line-by-line kinetic reveal. `lines` may contain React nodes.
export const KineticHeading = ({ lines, as: Tag = "h1", className = "", delay = 0, ...rest }) => (
  <Tag className={className} {...rest}>
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-[0.14em] -mb-[0.14em]">
        <motion.span
          className="block will-change-transform"
          initial={{ y: "112%", rotate: 1.5 }}
          animate={{ y: 0, rotate: 0 }}
          transition={{ duration: 1, ease: EASE, delay: delay + i * 0.12 }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </Tag>
);

// Fade-up on mount (hero copy / CTAs).
export const FadeUp = ({ children, delay = 0, y = 24, className = "", ...rest }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, ease: EASE, delay }}
    {...rest}
  >
    {children}
  </motion.div>
);

// Scroll-triggered reveal for sections and cards.
export const Reveal = ({ children, delay = 0, y = 32, className = "", once = true, as = "div", ...rest }) => {
  const Comp = motion[as] || motion.div;
  return (
    <Comp
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: "0px 0px -12% 0px" }}
      transition={{ duration: 0.85, ease: EASE, delay }}
      {...rest}
    >
      {children}
    </Comp>
  );
};

// Hero backdrop: slow scale-in on load + parallax drift on scroll.
export const HeroBackdrop = ({ image, position = "center" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.55]);
  return (
    <div ref={ref} className="absolute inset-0 -z-10 overflow-hidden" aria-hidden>
      <motion.div
        className="absolute inset-[-8%] bg-cover will-change-transform"
        style={{ backgroundImage: `url(${image})`, backgroundPosition: position, y, opacity }}
        initial={{ scale: 1.14 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.8, ease: EASE }}
      />
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute inset-0 grain" />
    </div>
  );
};
